import React, { useState, PropsWithChildren, ReactNode } from 'react'
import { Typography } from '@hermes/web-components'
import Accordion from './Accordion'
import { Wrapper, ContentContainer, Section, SectionContent } from './styles'
import Facts from './Facts'
import { QuickFactsAndQuestionsContent } from '../../../../types/quickFacts'

const AccordionView = ({
  question,
  answer,
  expandedDefault
}: {
  question: ReactNode
  answer: ReactNode
  expandedDefault?: boolean
}) => {
  const [expanded, setExpanded] = useState(expandedDefault ?? false)

  return (
    <Accordion
      expanded={expanded}
      onChange={(_: unknown, value: boolean | ((prevState: boolean) => boolean)) => setExpanded(value)}
    >
      <Accordion.Summary>
        <Typography fontSize="15px" fontWeight={expanded ? '500' : '400'} color="#2C3341">
          {question}
        </Typography>
      </Accordion.Summary>
      <Accordion.Details>
        {typeof answer === 'string' ? (
          <Typography
            dangerouslySetInnerHTML={{ __html: answer as string }}
            fontSize="13px"
            fontWeight="400"
            color="#2C3341"
          />
        ) : (
          <Typography fontSize="13px" fontWeight="400" color="#2C3341">
            {answer}
          </Typography>
        )}
      </Accordion.Details>
    </Accordion>
  )
}

const FactView = ({ title, value, icon }: { title: ReactNode; value: ReactNode; icon: ReactNode }) => {
  return (
    <Facts.Item>
      {icon}
      <div>
        <div style={{ display: 'inline' }}>
          <Typography
            component="span"
            fontSize="15px"
            fontWeight="400"
            lineHeight="20px"
            fontStyle="normal"
            color="#2C3341"
          >
            {title}
          </Typography>
        </div>
        <div style={{ display: 'inline', margin: '0 8px' }}>
          <Typography
            component="span"
            fontSize="15px"
            fontWeight="500"
            lineHeight="20px"
            fontStyle="normal"
            color="#2C3341"
          >
            {value}
          </Typography>
        </div>
      </div>
    </Facts.Item>
  )
}

const SectionTitle = ({ children }: PropsWithChildren) => (
  <Typography
    fontFamily="Poppins"
    color="#2C3341"
    variant="h2"
    fontSize="18px"
    fontWeight="500"
    fontStyle="normal"
    lineHeight="26px"
  >
    {children}
  </Typography>
)

const QuickFactsAndQuestions = ({
  data,
  containerStyle
}: {
  data: QuickFactsAndQuestionsContent
  containerStyle?: React.CSSProperties
}) => {
  const { facts, factsTitle, QAs, QAsTitle } = data ?? {}
  const factsCount = data?.facts?.length
  const questionsCount = data?.QAs?.length

  return factsCount || questionsCount ? (
    <Wrapper style={containerStyle}>
      <ContentContainer>
        {factsCount ? (
          <Section>
            <SectionContent>
              {factsTitle ? <SectionTitle>{factsTitle}</SectionTitle> : null}
              <Facts>
                {facts.map(({ title, value, icon }, key) => (
                  <FactView key={key} title={title} value={value} icon={icon} />
                ))}
              </Facts>
            </SectionContent>
          </Section>
        ) : null}
        {questionsCount ? (
          <Section>
            <SectionContent>
              {QAsTitle ? <SectionTitle>{QAsTitle}</SectionTitle> : null}
              <div>
                {QAs.map(({ question, answer }, key) => (
                  <AccordionView key={key} question={question} answer={answer} expandedDefault={key === 0} />
                ))}
              </div>
            </SectionContent>
          </Section>
        ) : null}
      </ContentContainer>
    </Wrapper>
  ) : null
}

export default QuickFactsAndQuestions
